// Reject a queued payout request (contract §9 pins this file's path, default export and
// `{payout, open, onClose}` props exactly). Requires a written reason of at least 5
// characters (validated by `StepUpConfirmDialog`'s `reasonMinLen`) and, being
// money-moving, a fresh step-up token (§4.1) before the API call is made.
import React from "react";
import { toast } from "sonner";
import { useLanguage, useT } from "../../lib/LanguageContext";
import { formatMoneyLedger } from "../../lib/formatMoney";
import defaultGatewayApi from "../api";
import { extractGatewayErrorCode } from "../errors";
import StepUpConfirmDialog from "./StepUpConfirmDialog";

/**
 * @param {import("../contract").PayoutRequest} payout
 * @param {boolean} open
 * @param {function} onClose (didMutate: boolean) => void
 * @param {object} [gatewayApi] DI seam (contract §9)
 */
export default function PayoutRejectDialog({ payout, open, onClose, gatewayApi = defaultGatewayApi }) {
  const t = useT("superadmin");
  const { lang } = useLanguage();

  const handleConfirm = async ({ reason, stepUpToken }) => {
    try {
      await gatewayApi.adminRejectPayout(payout.payoutRequestId, reason, stepUpToken);
      toast.success(t("gw.console.payouts.rejected"));
      return { ok: true };
    } catch (e) {
      return { ok: false, code: extractGatewayErrorCode(e) };
    }
  };

  if (!payout) return null;

  return (
    <StepUpConfirmDialog
      open={open}
      onClose={onClose}
      titleKey="gw.console.payouts.rejectTitle"
      titleVars={{ id: payout.payoutRequestId }}
      descKey="gw.console.payouts.rejectDesc"
      requireAck={false}
      requireReason
      reasonMinLen={5}
      reasonPlaceholderKey="gw.console.payouts.rejectReasonPlaceholder"
      onConfirm={handleConfirm}
      gatewayApi={gatewayApi}
    >
      <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm space-y-1.5" data-testid="reject-summary">
        <div className="flex justify-between">
          <span className="text-slate-500">{t("gw.console.payouts.colAmount")}</span>
          <span className="font-bold tabular-nums">{formatMoneyLedger(payout.amountCents, payout.currency, lang)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">{t("gw.console.payouts.colId")}</span>
          <span className="font-mono text-xs">{payout.payoutRequestId}</span>
        </div>
      </div>
    </StepUpConfirmDialog>
  );
}
